/**
 * Model thumbnails for the UI: one shared offscreen WebGL renderer draws each
 * registered model into an ordinary 2D <canvas> (store cards, team slots).
 * Browsers cap WebGL contexts, so the UI never gets a context of its own; it
 * declares `<canvas data-thumb="weapon:heavy">` and calls `thumbnails.mount(root)`.
 *
 * Specs: weapon:<WeaponKey> · item:<ItemKey> · player:<teamIndex>
 * Entries whose canvas leaves the document are pruned (and disposed) automatically,
 * so re-rendering a panel's innerHTML never leaks.
 */

import * as THREE from 'three';
import type { WeaponKey, ItemKey } from '../types';
import { TEAMS } from '../constants';
import { buildProjectileModel, buildHealthSmall, buildHealthLarge } from './projectileModels';
import { buildPlayerModel, bakePlayerModel, createAnimContext, updateAnimation } from './playerModel';
import { disposeObjectTree } from './meshBake';

export type ThumbSpec =
  | { kind: 'weapon'; weaponKey: WeaponKey }
  | { kind: 'item'; itemKey: ItemKey }
  | { kind: 'player'; teamIndex: number };

export interface ThumbObject {
  object: THREE.Object3D;
  spin: number;                     // radians per second about Y
  pitch: number;                    // camera elevation, radians
  margin: number;                   // framing slack (1 = tight)
  update?: (dt: number) => void;    // per-frame animation hook
}

/** Parse "kind:value" from a data attribute. */
export function parseThumbSpec(s: string): ThumbSpec | null {
  const [kind, value] = s.split(':');
  if (kind === 'weapon' && value) return { kind, weaponKey: value as WeaponKey };
  if (kind === 'item' && value) return { kind, itemKey: value as ItemKey };
  if (kind === 'player' && value !== undefined) return { kind, teamIndex: Number(value) || 0 };
  return null;
}

/** Build the displayable object for a spec. Pure Three.js construction, no renderer. */
export function buildThumbnailObject(spec: ThumbSpec): ThumbObject {
  switch (spec.kind) {
    case 'weapon': {
      const object = buildProjectileModel(spec.weaponKey);
      object.rotation.x = -0.35;                       // nose up a little so the body reads
      object.rotation.y = -0.6;                        // fixed three-quarter view: quieter than a spin
      return { object, spin: 0, pitch: 0.35, margin: 1.15 };
    }
    case 'item': {
      const object = spec.itemKey === 'healLarge' ? buildHealthLarge() : buildHealthSmall();
      object.rotation.y = -0.6;
      return { object, spin: 0, pitch: 0.5, margin: 1.2 };
    }
    case 'player': {
      const team = TEAMS[spec.teamIndex % TEAMS.length];
      const model = buildPlayerModel({ ...team });
      bakePlayerModel(model);
      const ctx = createAnimContext();            // idle: breathing bob + launcher drift
      const object = new THREE.Group();
      object.add(model.group);
      object.rotation.y = -0.5;
      return { object, spin: 0, pitch: 0.18, margin: 1.05, update: dt => updateAnimation(model, ctx, dt) };
    }
  }
}

interface Entry {
  canvas: HTMLCanvasElement;
  ctx2d: CanvasRenderingContext2D;
  thumb: ThumbObject;
  // framing, computed once from the object's bounds
  center: THREE.Vector3;
  radius: number;
}

export class ModelThumbnails {
  private renderer: THREE.WebGLRenderer | null = null;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(35, 1, 0.05, 100);
  private readonly entries = new Map<HTMLCanvasElement, Entry>();
  private raf = 0;
  private last = 0;
  private time = 0;

  private ensureRenderer(): THREE.WebGLRenderer | null {
    if (this.renderer) return this.renderer;
    try {
      const r = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      r.setClearColor(0x000000, 0);
      r.toneMapping = THREE.ACESFilmicToneMapping;
      r.toneMappingExposure = 1.05;
      this.renderer = r;
      this.scene.add(new THREE.AmbientLight(0x8090a0, 0.5));
      const sun = new THREE.DirectionalLight(0xffe8cc, 2.0);
      sun.position.set(3, 5, 4);
      this.scene.add(sun);
      return r;
    } catch {
      return null; // no WebGL: the UI keeps its text, thumbnails stay blank
    }
  }

  /** Register every `canvas[data-thumb]` under root (idempotent per canvas). */
  mount(root: ParentNode): void {
    const canvases = root.querySelectorAll<HTMLCanvasElement>('canvas[data-thumb]');
    for (const canvas of canvases) {
      if (this.entries.has(canvas)) continue;
      const spec = parseThumbSpec(canvas.dataset.thumb ?? '');
      const ctx2d = canvas.getContext('2d');
      if (!spec || !ctx2d) continue;
      const thumb = buildThumbnailObject(spec);
      const box = new THREE.Box3().setFromObject(thumb.object);
      const center = box.getCenter(new THREE.Vector3());
      const radius = Math.max(1e-3, box.getSize(new THREE.Vector3()).length() / 2);
      this.entries.set(canvas, { canvas, ctx2d, thumb, center, radius });
    }
    this.start();
  }

  /** Drop every entry (panel closed). */
  clear(): void {
    for (const e of this.entries.values()) disposeObjectTree(e.thumb.object);
    this.entries.clear();
    this.stop();
  }

  get size(): number { return this.entries.size; }

  private start(): void {
    if (this.raf || this.entries.size === 0) return;
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.frame);
  }

  private stop(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  private readonly frame = (now: number): void => {
    this.raf = 0;
    const dt = Math.min(0.05, (now - this.last) / 1000);
    this.last = now;
    this.time += dt;

    // prune canvases that left the document (panel re-rendered)
    for (const [canvas, e] of this.entries) {
      if (!canvas.isConnected) { disposeObjectTree(e.thumb.object); this.entries.delete(canvas); }
    }
    if (this.entries.size === 0) return;

    const r = this.ensureRenderer();
    if (r) {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      for (const e of this.entries.values()) {
        if (e.canvas.offsetParent === null) continue;   // hidden: skip the work
        const w = e.canvas.width, h = e.canvas.height;
        if (w === 0 || h === 0) continue;
        r.setSize(w / dpr, h / dpr, false);
        r.setPixelRatio(dpr);
        this.camera.aspect = w / h;
        this.camera.updateProjectionMatrix();

        e.thumb.update?.(dt);
        if (e.thumb.spin) e.thumb.object.rotation.y = this.time * e.thumb.spin;
        const dist = (e.radius * e.thumb.margin) / Math.sin((this.camera.fov * Math.PI) / 360);
        this.camera.position.set(
          e.center.x + Math.sin(0.4) * Math.cos(e.thumb.pitch) * dist,
          e.center.y + Math.sin(e.thumb.pitch) * dist,
          e.center.z + Math.cos(0.4) * Math.cos(e.thumb.pitch) * dist,
        );
        this.camera.lookAt(e.center);

        this.scene.add(e.thumb.object);
        r.render(this.scene, this.camera);
        this.scene.remove(e.thumb.object);
        e.ctx2d.clearRect(0, 0, w, h);
        e.ctx2d.drawImage(r.domElement, 0, 0, w, h);
      }
    }
    this.raf = requestAnimationFrame(this.frame);
  };
}

/** The UI's shared instance. */
export const thumbnails = new ModelThumbnails();

/** HTML for a thumbnail canvas (square, CSS-sized by `px`, backing store at 2× for sharpness). */
export function thumbCanvasHtml(spec: string, px: number, extraStyle = ''): string {
  const bs = px * 2;
  return `<canvas data-thumb="${spec}" width="${bs}" height="${bs}" style="width:${px}px; height:${px}px; display:block; ${extraStyle}"></canvas>`;
}
